import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import usePageTitle from "@/hooks/usePageTitle";
import { LEGAL } from "@/lib/legal";

const CONTACT_EMAIL = LEGAL.email;

export default function ContactPage() {
  const navigate = useNavigate();
  usePageTitle("تواصل معنا · بَلاغة");
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ kind: "idle", msg: "" });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMailto = () => {
    const sub = encodeURIComponent(form.subject || "رسالة من موقع بَلاغة");
    const body = encodeURIComponent(
      `الاسم: ${form.name}\nالبريد: ${form.email}\n\n${form.message}`
    );
    return `mailto:${CONTACT_EMAIL}?subject=${sub}&body=${body}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      setStatus({ kind: "error", msg: "الرجاء إدخال الاسم." });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setStatus({ kind: "error", msg: "الرجاء إدخال بريد إلكترونيّ صحيح." });
      return;
    }
    if (form.message.trim().length < 10) {
      setStatus({ kind: "error", msg: "الرسالة قصيرةٌ جدًّا (10 أحرف على الأقلّ)." });
      return;
    }
    setStatus({ kind: "sending", msg: "جارٍ الإرسال…" });
    try {
      // 1) save to backend
      await api.sendContact(form);
      // 2) fire mailto in a new tab/app
      window.location.href = buildMailto();
      setStatus({
        kind: "success",
        msg: "✅ تمّ حفظ رسالتك. وسيُفتح تطبيق البريد لإرسالها مباشرةً إلينا.",
      });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error("contact submit failed", err);
      setStatus({
        kind: "error",
        msg:
          "تعذّر الحفظ في الخادم. سيُفتح تطبيق البريد كنُسخةٍ احتياطيّة الآن.",
      });
      // Fallback: still open mailto so the user can send
      window.location.href = buildMailto();
    }
  };

  return (
    <div className="shell has-bottom-nav" data-testid="contact-page">
      <div className="page-header contact-hero">
        <div className="hero-emblem">✉️</div>
        <h1>
          تواصل <span className="gold">معنا</span>
        </h1>
        <p className="hero-sub">
          نُحبّ سَماع آراء المتعلّمين، الكُتّاب، والمؤسّسات. اكتب لنا رسالةً
          وسنردّ عليك في أقرب وقت.
        </p>
      </div>

      <div className="contact-grid">
        {/* ===== FORM ===== */}
        <form className="contact-card" onSubmit={onSubmit} data-testid="contact-form">
          <label>
            <span>الاسم الكامل *</span>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              required
              minLength={2}
              data-testid="contact-input-name"
            />
          </label>
          <label>
            <span>البريد الإلكترونيّ *</span>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              required
              data-testid="contact-input-email"
            />
          </label>
          <label>
            <span>الموضوع</span>
            <input
              type="text"
              value={form.subject}
              onChange={update("subject")}
              placeholder="استفسار، اقتراح، إبلاغ عن خطأ…"
              data-testid="contact-input-subject"
            />
          </label>
          <label>
            <span>الرسالة *</span>
            <textarea
              value={form.message}
              onChange={update("message")}
              required
              minLength={10}
              rows={6}
              data-testid="contact-input-message"
            />
          </label>

          {status.kind !== "idle" && (
            <div
              className={`contact-status contact-status-${status.kind}`}
              data-testid="contact-status"
            >
              {status.msg}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-gold contact-submit"
            disabled={status.kind === "sending"}
            data-testid="contact-submit-btn"
          >
            {status.kind === "sending" ? "جارٍ الإرسال…" : "إرسال الرسالة"}
          </button>
        </form>

        {/* ===== INFO ===== */}
        <aside className="contact-info" data-testid="contact-info">
          <h3>📬 معلومات التواصل</h3>
          <p>
            <strong>البريد:</strong>{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="gold">
              {CONTACT_EMAIL}
            </a>
          </p>
          <p>
            <strong>المنصّة:</strong> منصّة {LEGAL.app_name_ar} التعليميّة
          </p>
          <hr />
          <h3>🏛️ الجهة القانونيّة</h3>
          <p data-testid="contact-legal-entity" style={{ lineHeight: 1.85 }}>
            <strong>{LEGAL.entity_ar}</strong>
            <br />
            <span style={{ direction: "ltr", display: "inline-block" }}>
              {LEGAL.entity_en}
            </span>
            <br />
            السجلّ التجاريّ: <strong data-testid="contact-cr">{LEGAL.cr_number}</strong>
            <br />
            العنوان: {LEGAL.address_ar}
          </p>
          <hr />
          <h3>🤝 هل أنت جهة مؤسّسيّة؟</h3>
          <p>
            للشراكات والتعاون التعليميّ مع المدارس والجامعات والمراكز،
            استعمل النموذج المُخَصَّص:
          </p>
          <button
            className="btn btn-outline"
            onClick={() => navigate("/partnerships")}
            data-testid="contact-go-partnerships"
          >
            صفحة الشراكات →
          </button>
        </aside>
      </div>
    </div>
  );
}
